if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this.zoomScale = 1;
        this.bindEvents();
      }

      get slides() {
        return Array.from(this.querySelectorAll('.next-modal__slide:not([data-variant-hidden])'));
      }

      get thumbs() {
        return Array.from(this.querySelectorAll('.next-modal__thumb:not([data-variant-hidden])'));
      }

      // --- Mobile thumbnail overlay visibility ---
      // The thumbs sidebar auto-hides on mobile so it doesn't cover the image.
      // These helpers are no-ops visually on desktop (the CSS only reacts to the
      // `thumbs-hidden` class inside the mobile media query), but we still gate the
      // inactivity timer to mobile to avoid setting pointless timers.
      get isThumbOverlay() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      hideThumbs() {
        clearTimeout(this._thumbHideTimer);
        clearTimeout(this._thumbReappearTimer);
        this.classList.add('thumbs-hidden');
      }

      showThumbs() {
        clearTimeout(this._thumbReappearTimer);
        clearTimeout(this._thumbHideTimer);
        this.classList.remove('thumbs-hidden');
        // When not zoomed, tuck the thumbs away again a couple of seconds after
        // the last interaction so the image is unobstructed.
        if (this.isThumbOverlay && this.zoomScale <= 1) {
          this._thumbHideTimer = setTimeout(() => this.classList.add('thumbs-hidden'), 2000);
        }
      }

      // While zoomed, bring the thumbs back only once the image has been idle
      // (no panning/pinching) for 3s. Any new movement reschedules this, so the
      // thumbs never reappear mid-scroll.
      scheduleZoomIdleReappear() {
        clearTimeout(this._thumbReappearTimer);
        this._thumbReappearTimer = setTimeout(() => {
          if (this.zoomScale > 1) this.classList.remove('thumbs-hidden');
        }, 3000);
      }

      // A deliberate tap on the image toggles the thumbs. This is the only way a
      // finger gesture reveals them — dragging, swiping and panning never do.
      toggleThumbs() {
        if (this.classList.contains('thumbs-hidden')) this.showThumbs();
        else this.hideThumbs();
      }

      bindEvents() {
        // Close
        this.querySelector('.next-modal__close')?.addEventListener('click', () => this.hide());
        this.querySelector('.next-modal__overlay')?.addEventListener('click', () => this.hide());

        // Thumbnail clicks (delegated)
        this.addEventListener('click', (e) => {
          const thumb = e.target.closest('.next-modal__thumb');
          if (thumb) {
            this.goTo(parseInt(thumb.dataset.index));
            this.showThumbs();
          }
        });

        // Thumbnail nav arrows navigate slides
        this.querySelector('.next-modal__thumb-nav--up')?.addEventListener('click', () => { this.goTo(this.currentIndex - 1); this.showThumbs(); });
        this.querySelector('.next-modal__thumb-nav--down')?.addEventListener('click', () => { this.goTo(this.currentIndex + 1); this.showThumbs(); });

        // Keep the thumbs visible while the user is interacting with the list
        // itself (tapping/scrolling), then resume the auto-hide once they're done.
        const thumbsEl = this.querySelector('.next-modal__thumbs');
        if (thumbsEl) {
          const keepAlive = () => { clearTimeout(this._thumbHideTimer); this.classList.remove('thumbs-hidden'); };
          thumbsEl.addEventListener('touchstart', keepAlive, { passive: true });
          thumbsEl.addEventListener('touchmove', keepAlive, { passive: true });
          thumbsEl.addEventListener('touchend', () => this.showThumbs(), { passive: true });
        }

        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
          if (!this.hasAttribute('open')) return;
          if (e.key === 'Escape') {
            e.preventDefault();
            this.hide();
          }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            this.goTo(this.currentIndex - 1);
          }
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.goTo(this.currentIndex + 1);
          }
        });

        // Block background scrolling when modal is open
        this.addEventListener('wheel', (e) => {
          if (this.hasAttribute('open')) e.preventDefault();
        }, { passive: false });

        // Touch: swipe + pinch-to-zoom
        this.setupTouch();

        // Desktop: scroll-wheel zoom
        this.setupWheelZoom();
      }

      setupTouch() {
        const main = this.querySelector('.next-modal__main');
        if (!main) return;

        let startX = 0;
        let startY = 0;
        let isPinching = false;
        let wasPinching = false;
        let startDist = 0;
        let lastScale = 1;
        let translateX = 0;
        let translateY = 0;
        let lastTranslateX = 0;
        let lastTranslateY = 0;
        let startMidX = 0;
        let startMidY = 0;
        let lastTap = 0;
        let tapStartTime = 0;

        const getDistance = (t1, t2) => {
          const dx = t1.clientX - t2.clientX;
          const dy = t1.clientY - t2.clientY;
          return Math.sqrt(dx * dx + dy * dy);
        };

        const getMidpoint = (t1, t2) => ({
          x: (t1.clientX + t2.clientX) / 2,
          y: (t1.clientY + t2.clientY) / 2,
        });

        const getActiveImg = () => {
          const slide = this.querySelector('.next-modal__slide.is-active');
          return slide?.querySelector('.next-modal__img');
        };

        const applyTransform = (img) => {
          img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${this.zoomScale})`;
        };

        this.resetZoom = () => {
          const wasZoomed = this.zoomScale > 1;
          this.zoomScale = 1;
          lastScale = 1;
          translateX = 0;
          translateY = 0;
          lastTranslateX = 0;
          lastTranslateY = 0;
          const img = getActiveImg();
          if (img) {
            img.style.transform = '';
            img.style.transformOrigin = '';
          }
          // Only reveal thumbs when we've genuinely come back from a zoom, so a
          // plain swipe between slides (which also resets the transform) doesn't
          // flash them in.
          if (wasZoomed) this.showThumbs();
        };

        main.addEventListener(
          'touchstart',
          (e) => {
            wasPinching = false;
            // Any new touch supersedes a pending single-tap toggle (this is also
            // what cancels the toggle when the gesture turns into a double-tap).
            if (this._tapTimeout) clearTimeout(this._tapTimeout);

            if (e.touches.length === 1) {
              // Double-tap detection
              const now = Date.now();
              if (now - lastTap < 300) {
                e.preventDefault();
                const img = getActiveImg();
                if (this.zoomScale > 1) {
                  this.resetZoom();
                } else if (img) {
                  this.zoomScale = 2.5;
                  lastScale = this.zoomScale;
                  const rect = img.getBoundingClientRect();
                  img.style.transformOrigin = `${e.touches[0].clientX - rect.left}px ${e.touches[0].clientY - rect.top}px`;
                  applyTransform(img);
                  this.hideThumbs();
                }
                lastTap = 0;
                return;
              }
              lastTap = now;

              startX = e.touches[0].clientX;
              startY = e.touches[0].clientY;
              tapStartTime = now;

              if (this.zoomScale > 1) {
                // Beginning a pan while zoomed: keep thumbs hidden.
                this.hideThumbs();
                startMidX = e.touches[0].clientX;
                startMidY = e.touches[0].clientY;
                lastTranslateX = translateX;
                lastTranslateY = translateY;
              }
              // Not zoomed: do nothing here. Touching/dragging must NOT reveal the
              // thumbs — only a deliberate tap (handled on touchend) does.
            }

            if (e.touches.length === 2) {
              e.preventDefault();
              isPinching = true;
              wasPinching = true;
              this.hideThumbs();
              startDist = getDistance(e.touches[0], e.touches[1]);
              const mid = getMidpoint(e.touches[0], e.touches[1]);
              startMidX = mid.x;
              startMidY = mid.y;
              lastTranslateX = translateX;
              lastTranslateY = translateY;

              const img = getActiveImg();
              if (img && this.zoomScale <= 1) {
                const rect = img.getBoundingClientRect();
                img.style.transformOrigin = `${mid.x - rect.left}px ${mid.y - rect.top}px`;
              }
            }
          },
          { passive: false }
        );

        main.addEventListener(
          'touchmove',
          (e) => {
            if (e.touches.length === 2 && isPinching) {
              e.preventDefault();
              const dist = getDistance(e.touches[0], e.touches[1]);
              const mid = getMidpoint(e.touches[0], e.touches[1]);

              this.zoomScale = Math.max(1, Math.min(5, lastScale * (dist / startDist)));
              translateX = lastTranslateX + (mid.x - startMidX);
              translateY = lastTranslateY + (mid.y - startMidY);

              this.hideThumbs();
              const img = getActiveImg();
              if (img) applyTransform(img);
            } else if (e.touches.length === 1 && this.zoomScale > 1) {
              e.preventDefault();
              // Actively panning while zoomed: keep thumbs hidden and (re)arm the
              // 3s idle timer so they only return once movement stops.
              this.classList.add('thumbs-hidden');
              this.scheduleZoomIdleReappear();
              translateX = lastTranslateX + (e.touches[0].clientX - startMidX);
              translateY = lastTranslateY + (e.touches[0].clientY - startMidY);
              const img = getActiveImg();
              if (img) applyTransform(img);
            }
          },
          { passive: false }
        );

        main.addEventListener(
          'touchend',
          (e) => {
            // When all fingers lift while still zoomed, thumbs only return after
            // 3s of stillness. Not-zoomed reveal is handled by tap detection below.
            if (e.touches.length === 0 && this.zoomScale > 1) {
              this.scheduleZoomIdleReappear();
            }

            if (isPinching && e.touches.length < 2) {
              isPinching = false;
              lastScale = this.zoomScale;
              lastTranslateX = translateX;
              lastTranslateY = translateY;

              if (this.zoomScale <= 1) this.resetZoom();

              // Track remaining finger for pan
              if (e.touches.length === 1) {
                startMidX = e.touches[0].clientX;
                startMidY = e.touches[0].clientY;
                lastTranslateX = translateX;
                lastTranslateY = translateY;
              }
              return;
            }

            // Single-finger release (not part of a pinch): classify as swipe or tap.
            if (e.touches.length === 0 && !wasPinching) {
              const dx = e.changedTouches[0].clientX - startX;
              const dy = e.changedTouches[0].clientY - startY;
              const moved = Math.sqrt(dx * dx + dy * dy);
              const duration = Date.now() - tapStartTime;

              if (
                this.zoomScale <= 1 &&
                this.classList.contains('thumbs-hidden') &&
                Math.abs(dx) > Math.abs(dy) &&
                Math.abs(dx) > 50
              ) {
                // Horizontal swipe -> change slide. Only allowed while the thumbs
                // are tucked away, so the gallery doesn't slide out from under the
                // thumbnail strip while it's on screen.
                if (dx < 0) this.goTo(this.currentIndex + 1);
                else this.goTo(this.currentIndex - 1);
              } else if (moved < 10 && duration < 300) {
                // Deliberate tap -> toggle the thumbs. Deferred ~300ms so a
                // double-tap (zoom) cancels it (see touchstart) rather than
                // flashing the thumbs in.
                this._tapTimeout = setTimeout(() => this.toggleThumbs(), 300);
              }
            }
          },
          { passive: true }
        );
      }

      setupWheelZoom() {
        const main = this.querySelector('.next-modal__main');
        if (!main) return;

        let scale = 1;
        let translateX = 0;
        let translateY = 0;

        const getActiveImg = () => {
          const slide = this.querySelector('.next-modal__slide.is-active');
          return slide?.querySelector('.next-modal__img');
        };

        this._wheelResetZoom = () => {
          scale = 1;
          translateX = 0;
          translateY = 0;
          const img = getActiveImg();
          if (img) {
            img.style.transform = '';
            img.style.transformOrigin = '';
          }
        };

        main.addEventListener('wheel', (e) => {
          e.preventDefault();
          const img = getActiveImg();
          if (!img) return;

          const rect = img.getBoundingClientRect();
          const cursorX = e.clientX - rect.left;
          const cursorY = e.clientY - rect.top;

          const prevScale = scale;
          const delta = e.deltaY > 0 ? 0.9 : 1.1;
          scale = Math.max(1, Math.min(5, scale * delta));

          if (scale <= 1) {
            this._wheelResetZoom();
            return;
          }

          // Set transform origin on first zoom
          if (prevScale <= 1) {
            img.style.transformOrigin = `${cursorX}px ${cursorY}px`;
          }

          img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
        }, { passive: false });

        // Allow click-drag panning when zoomed via wheel
        let isDragging = false;
        let dragStartX = 0;
        let dragStartY = 0;
        let dragStartTX = 0;
        let dragStartTY = 0;

        main.addEventListener('mousedown', (e) => {
          if (scale <= 1) return;
          isDragging = true;
          dragStartX = e.clientX;
          dragStartY = e.clientY;
          dragStartTX = translateX;
          dragStartTY = translateY;
          main.style.cursor = 'grabbing';
          e.preventDefault();
        });

        document.addEventListener('mousemove', (e) => {
          if (!isDragging) return;
          translateX = dragStartTX + (e.clientX - dragStartX);
          translateY = dragStartTY + (e.clientY - dragStartY);
          const img = getActiveImg();
          if (img) {
            img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
          }
        });

        document.addEventListener('mouseup', () => {
          if (!isDragging) return;
          isDragging = false;
          main.style.cursor = '';
        });

        // Double-click to reset zoom
        main.addEventListener('dblclick', (e) => {
          const img = getActiveImg();
          if (!img) return;

          if (scale > 1) {
            this._wheelResetZoom();
          } else {
            scale = 2.5;
            const rect = img.getBoundingClientRect();
            img.style.transformOrigin = `${e.clientX - rect.left}px ${e.clientY - rect.top}px`;
            img.style.transform = `translate(0px, 0px) scale(${scale})`;
          }
        });

        // Sync with touch resetZoom
        const originalResetZoom = this.resetZoom;
        this.resetZoom = () => {
          originalResetZoom?.();
          scale = 1;
          translateX = 0;
          translateY = 0;
        };
      }

      show(index = 0, mediaId = null) {
        this.setAttribute('open', '');
        document.body.style.overflow = 'hidden';

        // Reindex after filtering
        const visibleSlides = Array.from(this.querySelectorAll('.next-modal__slide:not([data-variant-hidden])'));
        visibleSlides.forEach((s, i) => (s.dataset.index = i));
        const visibleThumbs = Array.from(this.querySelectorAll('.next-modal__thumb:not([data-variant-hidden])'));
        visibleThumbs.forEach((t, i) => (t.dataset.index = i));

        let targetIndex = 0;
        // If a media ID is provided, find the matching visible slide
        if (mediaId) {
          const found = visibleSlides.findIndex((s) => s.dataset.mediaId === mediaId);
          if (found >= 0) targetIndex = found;
        }

        this.goTo(targetIndex);
        this.focus();
        // Open with the thumbs tucked away so the large image is unobstructed;
        // a tap on the image brings them in. (No-op visually on desktop, where
        // the sidebar is always shown.)
        this.hideThumbs();
      }

      hide() {
        this.removeAttribute('open');
        document.body.style.overflow = '';
        this.resetZoom?.();
        clearTimeout(this._thumbHideTimer);
        clearTimeout(this._thumbReappearTimer);
        clearTimeout(this._tapTimeout);
        this.classList.remove('thumbs-hidden');
      }

      goTo(index) {
        const slides = this.slides;
        const thumbs = this.thumbs;
        if (slides.length === 0) return;
        if (index < 0) index = 0;
        if (index >= slides.length) index = slides.length - 1;

        // Reset zoom when changing slides
        if (index !== this.currentIndex) {
          this.resetZoom?.();
          this._wheelResetZoom?.();
        }

        this.currentIndex = index;

        slides.forEach((slide) => {
          slide.classList.toggle('is-active', parseInt(slide.dataset.index) === index);
        });

        thumbs.forEach((thumb) => {
          const isActive = parseInt(thumb.dataset.index) === index;
          thumb.classList.toggle('is-active', isActive);
          if (isActive) {
            thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });
      }
    }
  );
}
